import { logger } from '../logger/logger';
import {
  LOGGER_ACTION,
  LOGGER_FEATURE,
} from '../../constants/logger/logger-tags';
import {
  APPSFLYER_EVENT_NAME,
  AppsFlyerEventName,
} from '../../constants/integrations/appsflyer';
import { environment } from '../../environment';
import { IPlan } from '../../models/checkout.model';

type AppsFlyerFunction = (
  method: string,
  event: string,
  ...args: unknown[]
) => void;

declare global {
  interface Window {
    AF?: AppsFlyerFunction;
  }
}

const APPSFLYER_METHOD = {
  PBA: 'pba',
} as const;

const APPSFLYER_EVENT = {
  SET_CUSTOMER_USER_ID: 'setCustomerUserId',
  EVENT: 'event',
} as const;

const APPSFLYER_EVENT_TYPE = {
  EVENT: 'EVENT',
} as const;

export interface IAppsFlyerRevenue {
  amount: number;
  currency: string;
}

const getRevenueFields = (revenue?: IAppsFlyerRevenue) => {
  if (!revenue) {
    return {};
  }
  return {
    eventRevenue: revenue.amount,
    eventRevenueCurrency: revenue.currency,
  };
};

/**
 * Sends the user id to AppsFlyer as the customer user id.
 * The AF queue buffers this call until the sdk is ready, so it is safe
 * to call before the sdk has finished loading (see sdk snippet in index.html)
 */
export const setAppsFlyerCustomerUserId = (userId: string): void => {
  if (!environment.isProduction || !userId || !window.AF) {
    return;
  }
  try {
    window.AF(
      APPSFLYER_METHOD.PBA,
      APPSFLYER_EVENT.SET_CUSTOMER_USER_ID,
      userId
    );
  } catch (error) {
    logger.error('Failed to set AppsFlyer customer user id', error, {
      tags: {
        feature: LOGGER_FEATURE.APPSFLYER,
        action: LOGGER_ACTION.SET_CUSTOMER_USER_ID,
      },
    });
  }
};

export const sendAppsFlyerEvent = (
  eventName: AppsFlyerEventName,
  revenue?: IAppsFlyerRevenue
): void => {
  if (!environment.isProduction || !window.AF) {
    return;
  }
  try {
    window.AF(APPSFLYER_METHOD.PBA, APPSFLYER_EVENT.EVENT, {
      eventType: APPSFLYER_EVENT_TYPE.EVENT,
      eventName,
      ...getRevenueFields(revenue),
    });
  } catch (error) {
    logger.error('Failed to send AppsFlyer event', error, {
      tags: {
        feature: LOGGER_FEATURE.APPSFLYER,
        action: LOGGER_ACTION.TRACK,
      },
      extra: { eventName },
    });
  }
};

export const sendAppsFlyerSubscribeEvent = (plan: IPlan): void => {
  sendAppsFlyerEvent(APPSFLYER_EVENT_NAME.USER_SUBSCRIBE, {
    amount: plan.price,
    currency: plan.currency,
  });
};
